import type { GroupMemberPermissionsDto } from '../types/dtos';

/**
 * Group member-permission policy: a per-field allowlist controlling what everyone
 * EXCEPT the owner may edit on a group. The owner is always exempt and is the only
 * role allowed to change the policy itself. Shared by the API (enforcement) and both
 * clients (control gating) so there is a single source of truth.
 */

export type GroupRole = 'owner' | 'admin' | 'member';

export type GroupMemberPermissionKey = keyof GroupMemberPermissionsDto;

/**
 * Stable ordering for rendering the toggles in settings UIs.
 * `editSimplifyDebts` is deliberately absent: debt simplification is always
 * on and has no control left to gate. The key stays in the DTO for clients
 * that still send it.
 */
export const GROUP_MEMBER_PERMISSION_KEYS: GroupMemberPermissionKey[] = [
  'editImage',
  'editName',
  'editDescription',
  'editCategory',
  'editDates',
  'editCurrency',
  'editBudget',
  'editDefaultSplit',
];

/** Relaxed default applied to new groups and to any group with no stored policy. */
export const DEFAULT_GROUP_MEMBER_PERMISSIONS: GroupMemberPermissionsDto = {
  editImage: true,
  editName: true,
  editDescription: true,
  editCategory: true,
  editDates: true,
  editCurrency: true,
  editSimplifyDebts: true,
  editBudget: true,
  editDefaultSplit: true,
};

/**
 * Maps each editable group field (as it appears on Create/UpdateGroup payloads) to the
 * permission key that governs it. Fields not present here are NOT member-editable
 * (e.g. structural fields are handled by role checks, not this policy).
 */
export const GROUP_FIELD_TO_PERMISSION: Record<
  string,
  GroupMemberPermissionKey
> = {
  image: 'editImage',
  imageOriginal: 'editImage',
  imageFocalY: 'editImage',
  name: 'editName',
  description: 'editDescription',
  category: 'editCategory',
  categorySettings: 'editDates',
  currency: 'editCurrency',
  simplifyDebts: 'editSimplifyDebts',
  budgetAmount: 'editBudget',
  budgetPeriod: 'editBudget',
  budgetEnabled: 'editBudget',
  defaultSplitMethod: 'editDefaultSplit',
  defaultSplitConfig: 'editDefaultSplit',
};

/**
 * Merge a stored (possibly partial / undefined) policy over the relaxed defaults.
 * Missing keys default to `true` so policy-less groups and any forward-added keys stay
 * relaxed rather than silently locking out members.
 */
export function resolveMemberPermissions(
  policy?: Partial<GroupMemberPermissionsDto> | null
): GroupMemberPermissionsDto {
  if (!policy) return { ...DEFAULT_GROUP_MEMBER_PERMISSIONS };
  return {
    editImage: policy.editImage ?? true,
    editName: policy.editName ?? true,
    editDescription: policy.editDescription ?? true,
    editCategory: policy.editCategory ?? true,
    editDates: policy.editDates ?? true,
    editCurrency: policy.editCurrency ?? true,
    editSimplifyDebts: policy.editSimplifyDebts ?? true,
    editBudget: policy.editBudget ?? true,
    editDefaultSplit: policy.editDefaultSplit ?? true,
  };
}

/**
 * Effective per-field permissions for a specific user. Owners get everything; all other
 * roles are governed by the stored policy (admins included).
 */
export function effectivePermissionsForRole(
  role: GroupRole,
  policy?: Partial<GroupMemberPermissionsDto> | null
): GroupMemberPermissionsDto {
  if (role === 'owner') return { ...DEFAULT_GROUP_MEMBER_PERMISSIONS };
  return resolveMemberPermissions(policy);
}

/** Whether `role` may edit a given group field under `policy`. Owner is always allowed. */
export function canEditGroupField(
  field: string,
  role: GroupRole,
  policy?: Partial<GroupMemberPermissionsDto> | null
): boolean {
  if (role === 'owner') return true;
  const key = GROUP_FIELD_TO_PERMISSION[field];
  if (!key) return false; // unknown / non-policy field => not member-editable
  return resolveMemberPermissions(policy)[key];
}

/**
 * Whether this user can edit at least one field — used to decide if the group
 * settings/edit entry point should be shown to a non-owner.
 */
export function hasAnyEditPermission(
  role: GroupRole,
  policy?: Partial<GroupMemberPermissionsDto> | null
): boolean {
  return Object.values(effectivePermissionsForRole(role, policy)).some(Boolean);
}
